// src/components/Experiment/InferenceDialog.js
// Dialog for predicting classes of unknown samples with the trained model of an analysis.
// Unknown data may come from any experiment accessible to the user. Features are matched
// by POSITION (column names contain photo file names and differ between experiments).
// Layout follows AnalysisDialog (Accordion sections, feature/label filter blocks, statistics).
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Modal, Form, Button, Row, Col, Alert, Badge, Spinner, ListGroup, Table, InputGroup, Accordion, Collapse
} from 'react-bootstrap';
import {
  getAnalysisModelInfo,
  searchExperiments,
  getExperimentData
} from '../../api/photoProcessingApi';
import './AnalysisDialog.css';

// Max length of data_range (backend field is 500 chars)
const MAX_RANGE_LENGTH = 500;
// Max number of rows in one prediction run (backend MAX_INFERENCE_ROWS)
const MAX_INFERENCE_ROWS = 5000;
const IGNORE_VALUE = '';
// Internal key of rows with an empty label value (cannot collide with a real cell value)
const EMPTY_LABEL_KEY = '\u0000empty';

const ANALYSIS_TYPE_NAMES = {
  'lda': 'LDA',
  'logistic': 'Logistic Regression',
  'svm': 'SVM',
  'random_forest': 'Random Forest',
  'knn': 'K-Nearest Neighbors',
  'plsda': 'PLS-DA',
  'simca': 'SIMCA',
  'xgboost': 'Extreme Gradient Boosting',
  'bbc': 'Balanced Bagging Classifier',
  'brf': 'Balanced Random Forest'
};

const FEATURE_SELECTION_METHODS = {
  'anova': 'ANOVA F-test',
  'mutual_info': 'Mutual information'
};

// Same ordering as backend parse_data_range / DataTable
const CHANNEL_ORDER = {
  'R': 0, 'G': 1, 'B': 2, 'intensity': 3, 'rg_intensity': 4, 'rb_intensity': 5, 'gb_intensity': 6
};

const getOrderedColumns = (columnsMetadata) => {
  if (!columnsMetadata) return [];
  const sampleCols = [];
  const classCols = [];
  const dataCols = [];

  Object.entries(columnsMetadata).forEach(([colName, metadata]) => {
    if (metadata.type === 'sample_info') sampleCols.push(colName);
    else if (metadata.type === 'class') classCols.push(colName);
    else if (metadata.type === 'data') dataCols.push(colName);
  });

  dataCols.sort((a, b) => {
    const metaA = columnsMetadata[a];
    const metaB = columnsMetadata[b];
    const orderA = metaA.job_order !== undefined ? metaA.job_order : (metaA.job_id || 0);
    const orderB = metaB.job_order !== undefined ? metaB.job_order : (metaB.job_id || 0);
    if (orderA !== orderB) return orderA - orderB;
    const channelA = CHANNEL_ORDER[metaA.channel] !== undefined ? CHANNEL_ORDER[metaA.channel] : 99;
    const channelB = CHANNEL_ORDER[metaB.channel] !== undefined ? CHANNEL_ORDER[metaB.channel] : 99;
    return channelA - channelB;
  });

  return [...sampleCols, ...classCols, ...dataCols];
};

const indexToExcelColumn = (index) => {
  let result = '';
  while (index >= 0) {
    result = String.fromCharCode(65 + (index % 26)) + result;
    index = Math.floor(index / 26) - 1;
  }
  return result;
};

// Label cell value -> string, same as backend _cell_to_str (null/empty => '')
const cellToString = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number' && Number.isNaN(value)) return '';
  return String(value);
};

const labelKeyOf = (row, labelColumn) => {
  const value = row ? cellToString(row[labelColumn]) : '';
  return value === '' ? EMPTY_LABEL_KEY : value;
};

const displayLabel = (key) => (key === EMPTY_LABEL_KEY ? '(empty)' : key);

const normalizeLabel = (value) => String(value).trim().toLowerCase();

const describeFeature = (feature) => {
  if (!feature) return '—';
  return feature.channel || feature.calculate_type || '?';
};

// Group sorted indices into contiguous blocks
const toBlocks = (sortedIndices) => {
  const blocks = [];
  if (sortedIndices.length === 0) return blocks;
  let start = sortedIndices[0];
  let end = sortedIndices[0];
  for (let i = 1; i < sortedIndices.length; i++) {
    if (sortedIndices[i] === end + 1) {
      end = sortedIndices[i];
    } else {
      blocks.push({ start, end });
      start = sortedIndices[i];
      end = sortedIndices[i];
    }
  }
  blocks.push({ start, end });
  return blocks;
};

// Build excel-like range as AnalysisDialog.rebuildDataRange: every contiguous block of the
// selected rows combined with every contiguous chunk of the selected columns
const buildDataRange = (orderedColumns, selectedColumns, selectedRowIndices) => {
  if (selectedColumns.length === 0 || selectedRowIndices.length === 0) return '';
  const colIndices = selectedColumns
    .map(col => orderedColumns.indexOf(col))
    .filter(idx => idx >= 0)
    .sort((a, b) => a - b);
  if (colIndices.length === 0) return '';

  const rowBlocks = toBlocks([...selectedRowIndices].sort((a, b) => a - b));
  const colChunks = toBlocks(colIndices);

  const ranges = [];
  rowBlocks.forEach(rowBlock => {
    colChunks.forEach(colChunk => {
      ranges.push(
        `${indexToExcelColumn(colChunk.start)}${rowBlock.start + 1}:` +
        `${indexToExcelColumn(colChunk.end)}${rowBlock.end + 1}`
      );
    });
  });
  return ranges.join(', ');
};

const InferenceDialog = ({ show, onHide, analysis, onSubmit }) => {
  // Trained model info
  const [modelInfo, setModelInfo] = useState(null);
  const [modelLoading, setModelLoading] = useState(false);
  const [modelError, setModelError] = useState(null);

  // Source experiment search / selection
  const [searchQuery, setSearchQuery] = useState('');
  const [experiments, setExperiments] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [sourceExperiment, setSourceExperiment] = useState(null);

  // Source experiment data
  const [tableData, setTableData] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState(null);

  // Selection
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [isColumnFilterExpanded, setIsColumnFilterExpanded] = useState(true);
  const [rowStart, setRowStart] = useState(1);
  const [rowEnd, setRowEnd] = useState(1);
  const [labelColumn, setLabelColumn] = useState('sample_name');
  // Label values unchecked in "Filter by ... Labels" (empty set = all labels included)
  const [excludedLabels, setExcludedLabels] = useState(new Set());
  const [classMapping, setClassMapping] = useState({});
  const [averageReplicates, setAverageReplicates] = useState(false);
  const [name, setName] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const searchTimeoutRef = useRef(null);
  const dataRequestRef = useRef(0);

  const modelClasses = useMemo(() => modelInfo?.classes || [], [modelInfo]);
  const nFeatures = modelInfo?.n_features || 0;
  const featureSignature = useMemo(() => modelInfo?.feature_signature || [], [modelInfo]);
  const trainingLabelColumn = modelInfo?.training_label_column || null;
  const preprocessing = useMemo(() => modelInfo?.preprocessing || {}, [modelInfo]);
  const trainedOnAveraged = !!preprocessing.average_replicates;
  const featureSelection = preprocessing.feature_selection || null;
  // Positions (0-based, of the N model features) actually used by the model after feature selection
  const usedPositions = useMemo(() => {
    const indices = modelInfo?.selected_feature_indices;
    return Array.isArray(indices) ? new Set(indices) : null;
  }, [modelInfo]);
  const featureScores = modelInfo?.feature_selection_scores || null;

  // Reset the dialog and load model info on open
  useEffect(() => {
    if (!show || !analysis) return;

    setModelInfo(null);
    setModelError(null);
    setSearchQuery('');
    setExperiments([]);
    setSearchError(null);
    setSourceExperiment(null);
    setTableData(null);
    setDataError(null);
    setSelectedColumns([]);
    setIsColumnFilterExpanded(true);
    setLabelColumn('sample_name');
    setExcludedLabels(new Set());
    setClassMapping({});
    setAverageReplicates(false);
    setName('');
    setSubmitError(null);
    setSubmitting(false);

    let cancelled = false;
    setModelLoading(true);
    getAnalysisModelInfo(analysis.id)
      .then(info => { if (!cancelled) setModelInfo(info); })
      .catch(error => { if (!cancelled) setModelError(error.message); })
      .finally(() => { if (!cancelled) setModelLoading(false); });

    return () => { cancelled = true; };
  }, [show, analysis]);

  // Debounced experiment search
  useEffect(() => {
    if (!show) return undefined;

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setSearchLoading(true);
        setSearchError(null);
        const response = await searchExperiments({ q: searchQuery.trim(), filter_type: 'all', page: 1 });
        setExperiments(response.results || []);
      } catch (error) {
        setSearchError(error.message);
      } finally {
        setSearchLoading(false);
      }
    }, 400);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [show, searchQuery]);

  const orderedColumns = useMemo(
    () => getOrderedColumns(tableData?.columns_metadata),
    [tableData]
  );

  const dataColumns = useMemo(
    () => orderedColumns.filter(col => tableData?.columns_metadata[col]?.type === 'data'),
    [orderedColumns, tableData]
  );

  // Same options as AnalysisDialog.getLabelColumnOptions
  const labelColumnOptions = useMemo(() => {
    if (!tableData?.columns_metadata) return [];
    return Object.entries(tableData.columns_metadata)
      .filter(([, meta]) => meta.type === 'sample_info' || meta.type === 'class')
      .map(([colName, meta]) => ({
        value: colName,
        label: meta.type === 'class'
          ? `${meta.class_type || colName} (Classification)`
          : `${colName.replace('_', ' ')} (Sample Info)`
      }));
  }, [tableData]);

  const hasTrainingLabelColumn = !!(trainingLabelColumn &&
    labelColumnOptions.some(option => option.value === trainingLabelColumn));

  const nRows = tableData?.data_matrix?.length || 0;

  const handleSelectExperiment = async (experiment) => {
    const requestId = ++dataRequestRef.current;
    setSourceExperiment(experiment);
    setTableData(null);
    setDataError(null);
    setSelectedColumns([]);
    setExcludedLabels(new Set());
    setClassMapping({});
    setSubmitError(null);

    try {
      setDataLoading(true);
      const data = await getExperimentData(experiment.id);
      if (requestId !== dataRequestRef.current) return;

      if (!data || !data.columns_metadata || !Array.isArray(data.data_matrix) || data.data_matrix.length === 0) {
        setDataError('This experiment has no data table. Generate or import data first.');
        return;
      }
      if (data.status && data.status !== 'completed') {
        setDataError(`Experiment data is not ready (status: ${data.status}).`);
        return;
      }

      setTableData(data);

      // Defaults: all rows, first N data columns (same width as the model)
      const ordered = getOrderedColumns(data.columns_metadata);
      const dataCols = ordered.filter(col => data.columns_metadata[col]?.type === 'data');
      setSelectedColumns(dataCols.slice(0, nFeatures || dataCols.length));
      setRowStart(1);
      setRowEnd(data.data_matrix.length);

      // Average replicates as at training (needs sample_name in the source)
      setAverageReplicates(trainedOnAveraged && !!data.columns_metadata.sample_name);

      // Label column: the same column as at training if the source has it
      // (class mapping is then filled automatically), otherwise sample_name
      const isLabelCandidate = (col) =>
        ['sample_info', 'class'].includes(data.columns_metadata[col]?.type);
      const firstLabelColumn = ordered.find(isLabelCandidate);
      if (trainingLabelColumn && isLabelCandidate(trainingLabelColumn)) {
        setLabelColumn(trainingLabelColumn);
      } else {
        setLabelColumn(data.columns_metadata.sample_name ? 'sample_name' : (firstLabelColumn || 'sample_name'));
      }
    } catch (error) {
      if (requestId !== dataRequestRef.current) return;
      setDataError(
        error.status === 404 || error.response?.status === 404
          ? 'This experiment has no data table. Generate or import data first.'
          : (error.response?.data?.error || error.message || 'Failed to load experiment data')
      );
    } finally {
      if (requestId === dataRequestRef.current) setDataLoading(false);
    }
  };

  const handleUseCurrentExperiment = () => {
    if (!analysis) return;
    handleSelectExperiment({ id: analysis.experiment, name: 'Current experiment', is_owner: true });
  };

  // Selected columns in table order (this is the order in which features are passed to the model)
  const orderedSelectedColumns = useMemo(
    () => dataColumns.filter(col => selectedColumns.includes(col)),
    [dataColumns, selectedColumns]
  );

  const toggleColumn = (col) => {
    setSelectedColumns(prev => (prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col]));
  };

  const selectFirstN = () => setSelectedColumns(dataColumns.slice(0, nFeatures));
  const deselectAllColumns = () => setSelectedColumns([]);

  const safeRowStart = Math.max(1, Math.min(Number(rowStart) || 1, nRows || 1));
  const safeRowEnd = Math.max(1, Math.min(Number(rowEnd) || 1, nRows || 1));

  // Base rows (0-based) = "Rows from/to" range; the label filter works on top of it
  const baseRowIndices = useMemo(() => {
    if (!tableData || safeRowStart > safeRowEnd) return [];
    return Array.from({ length: safeRowEnd - safeRowStart + 1 }, (_, i) => safeRowStart - 1 + i);
  }, [tableData, safeRowStart, safeRowEnd]);

  // Label counts over the base rows (like originalCounts in AnalysisDialog)
  const labelCounts = useMemo(() => {
    const counts = {};
    if (!tableData || !labelColumn) return counts;
    baseRowIndices.forEach(rowIdx => {
      const key = labelKeyOf(tableData.data_matrix[rowIdx], labelColumn);
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [tableData, labelColumn, baseRowIndices]);

  const allLabelKeys = useMemo(
    () => Object.keys(labelCounts).sort((a, b) => displayLabel(a).localeCompare(displayLabel(b), undefined, { numeric: true })),
    [labelCounts]
  );

  // A new label column or base range selects all labels again (as in AnalysisDialog)
  useEffect(() => {
    setExcludedLabels(new Set());
  }, [labelColumn, safeRowStart, safeRowEnd, tableData]);

  const includedLabelKeys = allLabelKeys.filter(key => !excludedLabels.has(key));
  const areAllLabelsIncluded = includedLabelKeys.length === allLabelKeys.length;

  const handleLabelToggle = (key) => {
    setExcludedLabels(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleToggleAllLabels = () => {
    setExcludedLabels(areAllLabelsIncluded ? new Set(allLabelKeys) : new Set());
  };

  // Rows that are actually predicted
  const selectedRowIndices = useMemo(() => {
    if (!tableData) return [];
    return baseRowIndices.filter(rowIdx =>
      !excludedLabels.has(labelKeyOf(tableData.data_matrix[rowIdx], labelColumn)));
  }, [tableData, baseRowIndices, excludedLabels, labelColumn]);

  const dataRange = useMemo(
    () => buildDataRange(orderedColumns, orderedSelectedColumns, selectedRowIndices),
    [orderedColumns, orderedSelectedColumns, selectedRowIndices]
  );

  // Positional comparison with the model signature (soft hint, not blocking)
  const signatureComparison = useMemo(() => {
    if (!tableData) return [];
    const length = Math.max(featureSignature.length, orderedSelectedColumns.length);
    return Array.from({ length }, (_, idx) => {
      const expected = featureSignature[idx];
      const col = orderedSelectedColumns[idx];
      const meta = col ? tableData.columns_metadata[col] : null;
      const actual = meta ? { channel: meta.channel, calculate_type: meta.calculate_type } : null;
      // Positions dropped by feature selection do not affect the prediction
      const used = !usedPositions || usedPositions.has(idx);
      const score = Array.isArray(featureScores) ? featureScores[idx] : null;
      const mismatch = used && !!(expected && actual &&
        ((expected.channel || null) !== (actual.channel || null) ||
          (expected.calculate_type && actual.calculate_type && expected.calculate_type !== actual.calculate_type)));
      return { idx, expected, col, actual, mismatch, used, score };
    });
  }, [featureSignature, orderedSelectedColumns, tableData, usedPositions, featureScores]);

  const mismatchCount = signatureComparison.filter(item => item.mismatch).length;

  // Distinct (non-empty) label values of the selected rows - for class mapping
  const labelValues = useMemo(() => {
    if (!tableData || !labelColumn || labelColumn === 'sample_name') return [];
    const counts = new Map();
    selectedRowIndices.forEach(rowIdx => {
      const value = cellToString(tableData.data_matrix[rowIdx]?.[labelColumn]);
      if (value === '') return;
      counts.set(value, (counts.get(value) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true }));
  }, [tableData, labelColumn, selectedRowIndices]);

  // Auto-map label values that match a model class (case-insensitive, trimmed)
  useEffect(() => {
    if (labelValues.length === 0) {
      setClassMapping({});
      return;
    }
    // Keep manual choices for values that are still present, auto-map the new ones
    const byNormalized = new Map(modelClasses.map(cls => [normalizeLabel(cls), cls]));
    setClassMapping(prev => {
      const mapping = {};
      labelValues.forEach(({ value }) => {
        if (Object.prototype.hasOwnProperty.call(prev, value)) {
          mapping[value] = prev[value];
        } else {
          const match = byNormalized.get(normalizeLabel(value));
          if (match) mapping[value] = match;
        }
      });
      return mapping;
    });
  }, [labelValues, modelClasses]);

  const mappedCount = labelValues
    .filter(({ value }) => classMapping[value])
    .reduce((sum, { count }) => sum + count, 0);

  const selectedRowCount = selectedRowIndices.length;

  const hasSampleNameColumn = !!tableData?.columns_metadata?.sample_name;

  // Samples after averaging (same grouping as backend group_rows_by_sample: by sample_name,
  // rows without a sample name are not merged) and samples whose replicates map to different classes
  const replicateSummary = useMemo(() => {
    if (!tableData || !averageReplicates) return null;
    const groups = new Map();
    let unnamed = 0;
    selectedRowIndices.forEach(rowIdx => {
      const row = tableData.data_matrix[rowIdx];
      const sampleName = cellToString(row?.sample_name);
      const key = sampleName === '' ? `\u0000row${unnamed++}` : sampleName;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(row);
    });
    let conflicts = 0;
    if (labelColumn !== 'sample_name') {
      groups.forEach(rows => {
        const mapped = new Set(rows
          .map(row => classMapping[cellToString(row?.[labelColumn])])
          .filter(Boolean));
        if (mapped.size > 1) conflicts++;
      });
    }
    return { nSamples: groups.size, conflicts };
  }, [tableData, averageReplicates, selectedRowIndices, labelColumn, classMapping]);

  // Blocking validation (messages follow AnalysisDialog wording)
  const columnCountError = tableData && orderedSelectedColumns.length !== nFeatures
    ? (orderedSelectedColumns.length === 0
      ? 'No feature columns selected - select exactly ' + nFeatures + ' columns'
      : `Model expects ${nFeatures} feature columns, you selected ${orderedSelectedColumns.length}`)
    : null;
  const noLabelsSelected = !!tableData && allLabelKeys.length > 0 && includedLabelKeys.length === 0;

  const validationErrors = [];
  if (tableData) {
    if (columnCountError) validationErrors.push(columnCountError + '.');
    if (Number(rowStart) > Number(rowEnd)) {
      validationErrors.push('"Rows from" must be less than or equal to "Rows to".');
    }
    if (noLabelsSelected) {
      validationErrors.push('No labels selected - select at least one label.');
    }
    if (selectedRowCount > MAX_INFERENCE_ROWS) {
      validationErrors.push(`Too many rows selected (${selectedRowCount}). Maximum is ${MAX_INFERENCE_ROWS}.`);
    }
    if (dataRange.length > MAX_RANGE_LENGTH) {
      validationErrors.push(
        `Selected rows/columns combination produces a range specification that is too long ` +
        `(${dataRange.length} characters, limit ${MAX_RANGE_LENGTH}). Deselect fewer scattered ` +
        `columns or simplify label filtering.`
      );
    }
  }

  const canUseModel = modelInfo && modelInfo.has_model && modelInfo.supported !== false;
  const canSubmit = canUseModel && tableData && sourceExperiment && dataRange &&
    validationErrors.length === 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    const mapping = {};
    Object.entries(classMapping).forEach(([raw, cls]) => {
      if (cls) mapping[raw] = cls;
    });

    try {
      setSubmitting(true);
      setSubmitError(null);
      await onSubmit({
        source_experiment: sourceExperiment.id,
        data_range: dataRange,
        label_column: labelColumn || 'sample_name',
        class_mapping: labelColumn === 'sample_name' ? {} : mapping,
        average_replicates: averageReplicates,
        name: name.trim()
      });
    } catch (error) {
      setSubmitError(error.message || 'Failed to start prediction');
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------------------------------------------------- Model
  const renderModelSection = () => {
    if (modelLoading) {
      return (
        <div className="text-muted">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading trained model info...
        </div>
      );
    }
    if (modelError) {
      return <Alert variant="danger" className="mb-0">{modelError}</Alert>;
    }
    if (!modelInfo) return null;
    if (modelInfo.supported === false) {
      return (
        <Alert variant="warning" className="mb-0">
          Prediction of unknown samples is not supported for this analysis type.
        </Alert>
      );
    }
    if (!modelInfo.has_model) {
      return (
        <Alert variant="warning" className="mb-0">
          <i className="bi bi-exclamation-triangle me-2"></i>
          This analysis has no saved trained model. Rerun the analysis to enable predictions.
        </Alert>
      );
    }

    return (
      <>
        <Table size="sm" className="mb-2">
          <tbody>
            <tr>
              <td><strong>Algorithm:</strong></td>
              <td className="text-end">
                <Badge bg="secondary">{ANALYSIS_TYPE_NAMES[modelInfo.algorithm] || modelInfo.algorithm}</Badge>
              </td>
            </tr>
            <tr>
              <td><strong>Trained on experiment:</strong></td>
              <td className="text-end">{modelInfo.training_experiment_name || '—'}</td>
            </tr>
            <tr>
              <td><strong>Label column:</strong></td>
              <td className="text-end">
                <Badge bg="primary">{trainingLabelColumn || '—'}</Badge>
                {modelInfo.training_label_column_display &&
                  modelInfo.training_label_column_display !== trainingLabelColumn && (
                    <small className="text-muted ms-1">({modelInfo.training_label_column_display})</small>
                )}
              </td>
            </tr>
            <tr>
              <td><strong>Training data range:</strong></td>
              <td className="text-end"><code>{modelInfo.training_data_range || '—'}</code></td>
            </tr>
            <tr>
              <td><strong>Classes:</strong></td>
              <td className="text-end">
                {modelClasses.map(cls => (
                  <Badge key={cls} bg="info" className="ms-1">{cls}</Badge>
                ))}
              </td>
            </tr>
            <tr>
              <td><strong>Feature columns:</strong></td>
              <td className="text-end">
                <Badge bg="success">{nFeatures}</Badge>
                <span className="font-monospace small ms-2 text-break">
                  {featureSignature.map(describeFeature).join(', ')}
                </span>
              </td>
            </tr>
            <tr>
              <td colSpan={2} className="bg-light small fw-bold">Preprocessing at training</td>
            </tr>
            <tr>
              <td><strong>Average replicates:</strong></td>
              <td className="text-end">
                {trainedOnAveraged
                  ? <Badge bg="warning" text="dark">Yes (by sample_name)</Badge>
                  : <Badge bg="secondary">No</Badge>}
              </td>
            </tr>
            <tr>
              <td><strong>Feature selection:</strong></td>
              <td className="text-end">
                {featureSelection ? (
                  <>
                    <Badge bg="info">
                      {FEATURE_SELECTION_METHODS[featureSelection.method] || featureSelection.method}, k = {featureSelection.k}
                    </Badge>
                    <small className="text-muted ms-1">
                      {featureSelection.n_selected} of {featureSelection.n_total} columns used
                    </small>
                  </>
                ) : <Badge bg="secondary">None</Badge>}
              </td>
            </tr>
            <tr>
              <td><strong>Scaling:</strong></td>
              <td className="text-end">
                {preprocessing.scaling
                  ? <Badge bg="info">Standardized inside the model</Badge>
                  : <Badge bg="secondary">None</Badge>}
              </td>
            </tr>
          </tbody>
        </Table>
        <Form.Text className="text-muted d-block">
          Columns are matched by position, not by name: select feature columns of the unknown
          data in the same order of channels as used for training.
        </Form.Text>
        <Form.Text className="text-muted d-block">
          The same preprocessing is applied to the unknown data: features selected at training are
          taken by position, scaling uses the training mean/std (it is part of the model), replicates
          are averaged by sample_name if enabled below.
        </Form.Text>
      </>
    );
  };

  // ---------------------------------------------------------------- Source experiment
  const renderExperimentPicker = () => (
    <Row className="mb-3">
      <Col>
        <Form.Group>
          <Form.Label>Source Experiment</Form.Label>
          {sourceExperiment ? (
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Badge bg="info" className="fs-6 fw-normal">
                <i className="bi bi-folder2-open me-1"></i>
                {sourceExperiment.name}
              </Badge>
              {dataLoading && <Spinner animation="border" size="sm" />}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  dataRequestRef.current++;
                  setSourceExperiment(null);
                  setTableData(null);
                  setDataError(null);
                  setDataLoading(false);
                }}
              >
                Change
              </Button>
            </div>
          ) : (
            <>
              <InputGroup className="mb-2">
                <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
                <Form.Control
                  placeholder="Search experiments by name or owner..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <Button variant="outline-secondary" onClick={handleUseCurrentExperiment}>
                  Use current experiment
                </Button>
              </InputGroup>
              {searchError && <Alert variant="danger" className="py-1 small">{searchError}</Alert>}
              <ListGroup style={{ maxHeight: '220px', overflowY: 'auto' }}>
                {searchLoading && experiments.length === 0 && (
                  <ListGroup.Item className="small text-muted">
                    <Spinner animation="border" size="sm" className="me-2" />Searching...
                  </ListGroup.Item>
                )}
                {!searchLoading && experiments.length === 0 && (
                  <ListGroup.Item className="small text-muted">No experiments found</ListGroup.Item>
                )}
                {experiments.map(exp => (
                  <ListGroup.Item
                    key={exp.id}
                    action
                    className="py-1 small d-flex justify-content-between align-items-center"
                    onClick={() => handleSelectExperiment(exp)}
                  >
                    <span className="text-truncate me-2">
                      {exp.name}
                      {exp.id === analysis?.experiment && (
                        <Badge bg="light" text="dark" className="ms-2 border">current</Badge>
                      )}
                    </span>
                    <span className="text-nowrap">
                      <small className="text-muted me-2">{exp.user_full_name || exp.user_email}</small>
                      <Badge bg={exp.is_owner ? 'secondary' : 'info'}>{exp.is_owner ? 'mine' : 'shared'}</Badge>
                    </span>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </>
          )}
          <Form.Text className="text-muted">
            Any experiment you can view (own or shared). Showing the first 20 matches - refine the search to find others.
          </Form.Text>
          {dataError && <Alert variant="danger" className="py-2 mt-2 mb-0 small">{dataError}</Alert>}
        </Form.Group>
      </Col>
    </Row>
  );

  // ---------------------------------------------------------------- Feature columns
  const renderFeatureColumns = () => {
    const selectedCount = orderedSelectedColumns.length;
    const selectedBadge = selectedCount === nFeatures ? 'success' : (selectedCount === 0 ? 'danger' : 'warning');

    return (
      <Row className="mb-3">
        <Col>
          <Form.Group>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Button
                variant="link"
                className="p-0 text-decoration-none text-dark d-flex align-items-center"
                onClick={() => setIsColumnFilterExpanded(prev => !prev)}
                aria-expanded={isColumnFilterExpanded}
              >
                <i className={`bi bi-chevron-${isColumnFilterExpanded ? 'down' : 'right'} me-2`}></i>
                <Form.Label className="mb-0" as="span">
                  <span>
                    Feature Columns
                    <Badge bg="info" className="ms-2">{nFeatures} required</Badge>
                    <Badge bg={selectedBadge} className="ms-2">{selectedCount} selected</Badge>
                    {featureSelection && usedPositions && (
                      <Badge bg="primary" className="ms-2">{usedPositions.size} used by model</Badge>
                    )}
                    {mismatchCount > 0 && (
                      <Badge bg="warning" text="dark" className="ms-2">{mismatchCount} channel mismatch</Badge>
                    )}
                  </span>
                </Form.Label>
              </Button>
              <div className="d-flex gap-2">
                <Button variant="outline-secondary" size="sm" onClick={selectFirstN}>
                  Select first {nFeatures}
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={deselectAllColumns}>
                  Deselect All
                </Button>
              </div>
            </div>

            {columnCountError && (
              <Form.Text className="text-danger d-block mb-2">{columnCountError}</Form.Text>
            )}

            <Collapse in={isColumnFilterExpanded}>
              <div>
                <div className="label-filter-container p-3 border rounded bg-light">
                  {featureSelection && usedPositions && (
                    <Alert variant="light" className="border py-2 small">
                      <i className="bi bi-funnel me-1"></i>
                      Feature selection ({FEATURE_SELECTION_METHODS[featureSelection.method] || featureSelection.method},
                      k = {featureSelection.k}) was applied at training: the model uses {usedPositions.size} of {nFeatures}{' '}
                      positions (marked "Used" below). All {nFeatures} columns must still be selected - the used
                      features are taken by their positions in this order.
                    </Alert>
                  )}
                  <Form.Text className="text-muted d-block mb-2">
                    Check exactly {nFeatures} columns. They are passed to the model in table order.
                  </Form.Text>
                  <div className="row">
                    {dataColumns.map(col => {
                      const meta = tableData.columns_metadata[col];
                      const letter = indexToExcelColumn(orderedColumns.indexOf(col));
                      return (
                        <div key={col} className="col-6 col-md-4 col-lg-3 mb-2">
                          <div className="d-flex align-items-center">
                            <Form.Check
                              type="checkbox"
                              id={`inference-col-${col}`}
                              checked={selectedColumns.includes(col)}
                              onChange={() => toggleColumn(col)}
                              className="me-2"
                            />
                            <Form.Label htmlFor={`inference-col-${col}`} className="mb-0 flex-grow-1">
                              <span className="text-truncate d-block" title={col}>
                                <span className="text-muted me-1">{letter}</span>{col}
                              </span>
                            </Form.Label>
                            {meta?.channel && <Badge bg="secondary" pill>{meta.channel}</Badge>}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="border rounded bg-white mt-2" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                    <Table size="sm" className="mb-0 small">
                      <thead className="table-light" style={{ position: 'sticky', top: 0 }}>
                        <tr>
                          <th>#</th>
                          <th>Model (training)</th>
                          <th>Selected column</th>
                          {featureSelection && usedPositions && (
                            <>
                              <th className="text-center">Used</th>
                              <th className="text-end">Score</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {signatureComparison.map(item => (
                          <tr
                            key={item.idx}
                            className={item.mismatch ? 'table-warning' : (item.used ? '' : 'text-muted')}
                          >
                            <td>{item.idx + 1}</td>
                            <td>
                              {item.expected ? (
                                <>
                                  <Badge bg="secondary" className="me-1">{describeFeature(item.expected)}</Badge>
                                  <span className="text-muted">{item.expected.name}</span>
                                </>
                              ) : <span className="text-danger">— (extra)</span>}
                            </td>
                            <td>
                              {item.col ? (
                                <>
                                  <Badge
                                    bg={item.mismatch ? 'warning' : 'secondary'}
                                    text={item.mismatch ? 'dark' : undefined}
                                    className="me-1"
                                  >
                                    {describeFeature(item.actual)}
                                  </Badge>
                                  {item.col}
                                </>
                              ) : <span className="text-danger">— (missing)</span>}
                            </td>
                            {featureSelection && usedPositions && (
                              <>
                                <td className="text-center">
                                  {item.used
                                    ? <Badge bg="success"><i className="bi bi-check-lg"></i></Badge>
                                    : <span className="text-muted">—</span>}
                                </td>
                                <td className="text-end">
                                  {typeof item.score === 'number' ? item.score.toFixed(4) : '—'}
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  {mismatchCount > 0 && (
                    <Form.Text className="text-warning d-block">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      {mismatchCount} position(s) used by the model have a different channel than at training. Check that this is intended.
                    </Form.Text>
                  )}
                </div>
              </div>
            </Collapse>
          </Form.Group>
        </Col>
      </Row>
    );
  };

  // ---------------------------------------------------------------- Rows + label column
  const renderRowsAndLabelColumn = () => (
    <Row className="mb-3">
      <Col md={5}>
        <Form.Group>
          <Form.Label>Rows</Form.Label>
          <InputGroup>
            <InputGroup.Text>from</InputGroup.Text>
            <Form.Control
              type="number"
              min={1}
              max={nRows}
              value={rowStart}
              onChange={(e) => setRowStart(e.target.value)}
            />
            <InputGroup.Text>to</InputGroup.Text>
            <Form.Control
              type="number"
              min={1}
              max={nRows}
              value={rowEnd}
              onChange={(e) => setRowEnd(e.target.value)}
            />
          </InputGroup>
          <Form.Text className="text-muted">
            Base range of rows ({nRows} in the table). Rows can be further filtered by labels below.
          </Form.Text>
        </Form.Group>
      </Col>
      <Col md={7}>
        <Form.Group>
          <Form.Label>Label Column</Form.Label>
          <Form.Select
            value={labelColumn}
            onChange={(e) => setLabelColumn(e.target.value)}
          >
            {labelColumnOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted d-block">
            Model was trained on label column <Badge bg="primary">{trainingLabelColumn || '—'}</Badge>
            {hasTrainingLabelColumn && labelColumn !== trainingLabelColumn && (
              <Button
                variant="link"
                size="sm"
                className="p-0 ms-2 align-baseline"
                onClick={() => setLabelColumn(trainingLabelColumn)}
              >
                Use the same column
              </Button>
            )}
          </Form.Text>
          <Form.Text className="text-muted d-block">
            With <code>sample_name</code> only predictions are shown. With a class column mapped to
            model classes, accuracy, precision, recall, F1 and a confusion matrix are also calculated.
          </Form.Text>
        </Form.Group>
      </Col>
    </Row>
  );

  // ---------------------------------------------------------------- Label filter (as AnalysisDialog)
  const renderLabelFilter = () => {
    if (!labelColumn || allLabelKeys.length === 0) return null;
    const selectedBadge = areAllLabelsIncluded ? 'success' : (includedLabelKeys.length === 0 ? 'danger' : 'warning');

    return (
      <Row className="mb-3">
        <Col>
          <Form.Group>
            <div className="d-flex justify-content-between align-items-start mb-2">
              <Form.Label className="mb-0">
                <span>
                  Filter by {labelColumn} Labels
                  <Badge bg="info" className="ms-2">{allLabelKeys.length} total</Badge>
                  <Badge bg={selectedBadge} className="ms-2">{includedLabelKeys.length} selected</Badge>
                </span>
              </Form.Label>
              <Button variant="outline-secondary" size="sm" onClick={handleToggleAllLabels}>
                {areAllLabelsIncluded ? 'Deselect All' : 'Select All'}
              </Button>
            </div>

            {noLabelsSelected && (
              <Form.Text className="text-danger d-block mb-2">
                No labels selected - select at least one label
              </Form.Text>
            )}

            <div className="label-filter-container p-3 border rounded bg-light">
              <Form.Text className="text-muted d-block mb-2">
                Uncheck labels to exclude their rows from prediction
              </Form.Text>
              <div className="row" style={{ maxHeight: '240px', overflowY: 'auto' }}>
                {allLabelKeys.map((key, idx) => (
                  <div key={key} className="col-6 col-md-4 col-lg-3 mb-2">
                    <div className="d-flex align-items-center">
                      <Form.Check
                        type="checkbox"
                        id={`inference-label-${idx}`}
                        checked={!excludedLabels.has(key)}
                        onChange={() => handleLabelToggle(key)}
                        className="me-2"
                      />
                      <Form.Label htmlFor={`inference-label-${idx}`} className="mb-0 flex-grow-1">
                        <span
                          className={`text-truncate d-block ${key === EMPTY_LABEL_KEY ? 'fst-italic text-muted' : ''}`}
                          title={displayLabel(key)}
                        >
                          {displayLabel(key)}
                        </span>
                      </Form.Label>
                      <Badge bg={labelCounts[key] > 0 ? 'primary' : 'secondary'} pill>
                        {labelCounts[key]}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Form.Group>
        </Col>
      </Row>
    );
  };

  // ---------------------------------------------------------------- Averaging of technical replicates
  const renderAveraging = () => {
    const differsFromTraining = averageReplicates !== trainedOnAveraged;
    return (
      <Row className="mb-3">
        <Col>
          <Form.Group>
            <Form.Check
              type="switch"
              id="inference-average-replicates"
              label="Average technical replicates (by sample_name)"
              checked={averageReplicates}
              disabled={!hasSampleNameColumn}
              onChange={(e) => setAverageReplicates(e.target.checked)}
            />
            <Form.Text className="text-muted d-block">
              {trainedOnAveraged
                ? 'The model was trained on replicates averaged by sample_name.'
                : 'The model was trained on individual rows (replicates were not averaged).'}
              {' '}Rows with the same sample_name are averaged into one sample before prediction;
              rows without a sample name are predicted separately.
            </Form.Text>
            {!hasSampleNameColumn && (
              <Form.Text className="text-muted d-block">
                The source experiment has no sample_name column - averaging is not available.
              </Form.Text>
            )}
            {hasSampleNameColumn && differsFromTraining && (
              <Form.Text className="text-warning d-block">
                <i className="bi bi-exclamation-triangle me-1"></i>
                {trainedOnAveraged
                  ? 'Differs from training: individual replicates are noisier than the averaged samples the model was trained on.'
                  : 'Differs from training: the model has not seen averaged samples.'}
              </Form.Text>
            )}
          </Form.Group>
        </Col>
      </Row>
    );
  };

  // ---------------------------------------------------------------- Statistics + preview (as AnalysisDialog)
  const renderStatistics = () => {
    const distribution = includedLabelKeys
      .map(key => `${displayLabel(key)}: ${labelCounts[key]}`)
      .join(', ');

    return (
      <>
        <div className="stats-container">
          <Alert variant={selectedRowCount === 0 ? 'danger' : 'info'} className="mt-3">
            <strong>Label Statistics:</strong><br/>
            <div className="mb-2">
              <Badge bg="primary" className="me-2">
                {includedLabelKeys.length} unique labels
              </Badge>
              <Badge bg="secondary" className="me-2">
                {selectedRowCount} total samples
              </Badge>
              {includedLabelKeys.includes(EMPTY_LABEL_KEY) && (
                <Badge bg="warning" text="dark" className="me-2">Empty values included</Badge>
              )}
            </div>
            <div className="stats-text">
              Label distribution: {distribution || '—'}
            </div>
          </Alert>
        </div>
        <div className="preview-container">
          <Alert variant="info" className="mt-3 mb-0">
            <strong>Data Preview:</strong><br/>
            <div className="mb-2">
              <Badge bg="secondary" className="me-2">
                {selectedRowCount} rows × {orderedSelectedColumns.length} feature columns
              </Badge>
              <Badge bg={orderedSelectedColumns.length === nFeatures ? 'success' : 'danger'} className="me-2">
                {nFeatures} required by the model
              </Badge>
              {replicateSummary && (
                <Badge bg="warning" text="dark">
                  {selectedRowCount} rows → {replicateSummary.nSamples} samples after averaging
                </Badge>
              )}
            </div>
            {replicateSummary && replicateSummary.conflicts > 0 && (
              <div className="small text-danger mb-1">
                <i className="bi bi-exclamation-triangle me-1"></i>
                {replicateSummary.conflicts} sample(s) have replicates mapped to different classes - they are
                predicted, but excluded from the metrics.
              </div>
            )}
            <div className="small text-break">
              Data range: <code>{dataRange || '—'}</code>
            </div>
          </Alert>
        </div>
      </>
    );
  };

  // ---------------------------------------------------------------- Class mapping
  const renderClassMapping = () => (
    <>
      {labelValues.length === 0 ? (
        <Alert variant="light" className="border py-2 small mb-0">
          No values in the selected rows of this column.
        </Alert>
      ) : (
        <div className="border rounded" style={{ maxHeight: '260px', overflowY: 'auto' }}>
          <Table size="sm" className="mb-0 small align-middle">
            <thead className="table-light" style={{ position: 'sticky', top: 0 }}>
              <tr>
                <th>Value in "{labelColumn}"</th>
                <th>Rows</th>
                <th>Model class</th>
              </tr>
            </thead>
            <tbody>
              {labelValues.map(({ value, count }) => (
                <tr key={value}>
                  <td>{value}</td>
                  <td><Badge bg="primary" pill>{count}</Badge></td>
                  <td>
                    <Form.Select
                      size="sm"
                      value={classMapping[value] || IGNORE_VALUE}
                      onChange={(e) => setClassMapping(prev => ({ ...prev, [value]: e.target.value }))}
                    >
                      <option value={IGNORE_VALUE}>Ignore (no metrics)</option>
                      {modelClasses.map(cls => (
                        <option key={cls} value={cls}>{cls}</option>
                      ))}
                    </Form.Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
      <Form.Text className="text-muted">
        Values matching a model class are mapped automatically. Ignored rows still get predictions,
        but are excluded from the aggregated metrics.
      </Form.Text>
    </>
  );

  const showClassMapping = !!tableData && labelColumn !== 'sample_name';

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-magic me-2"></i>
          Predict Unknown Samples{analysis ? ` — ${analysis.name}` : ''}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={(e) => e.preventDefault()}>
          {/* Basic Configuration */}
          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Prediction Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder={`Prediction on ${sourceExperiment?.name || 'experiment'}`}
                  value={name}
                  maxLength={200}
                  onChange={(e) => setName(e.target.value)}
                />
                <Form.Text className="text-muted">Optional</Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Accordion className="mb-3" defaultActiveKey={['0', '1', '2']} alwaysOpen>
            <Accordion.Item eventKey="0">
              <Accordion.Header>Trained Model</Accordion.Header>
              <Accordion.Body>
                {renderModelSection()}
              </Accordion.Body>
            </Accordion.Item>

            {canUseModel && (
              <Accordion.Item eventKey="1">
                <Accordion.Header>Data Selection</Accordion.Header>
                <Accordion.Body>
                  {renderExperimentPicker()}
                  {tableData && (
                    <>
                      {renderFeatureColumns()}
                      {renderRowsAndLabelColumn()}
                      {renderLabelFilter()}
                      {renderAveraging()}
                      {renderStatistics()}
                    </>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            )}

            {canUseModel && showClassMapping && (
              <Accordion.Item eventKey="2">
                <Accordion.Header>
                  <span>
                    Class Mapping
                    <Badge bg={mappedCount > 0 ? 'success' : 'secondary'} className="ms-2">
                      {mappedCount} of {selectedRowCount} rows mapped
                    </Badge>
                  </span>
                </Accordion.Header>
                <Accordion.Body>
                  {renderClassMapping()}
                </Accordion.Body>
              </Accordion.Item>
            )}
          </Accordion>

          {tableData && validationErrors.length > 0 && (
            <Alert variant="danger" className="mb-0">
              {validationErrors.map(message => <div key={message}>{message}</div>)}
            </Alert>
          )}

          {submitError && <Alert variant="danger" className="mt-2 mb-0">{submitError}</Alert>}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Starting...
            </>
          ) : (
            <>
              <i className="bi bi-play-fill me-1"></i>
              Start Prediction
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InferenceDialog;
